import { AnimationEvent, FC, FocusEvent, KeyboardEvent, useEffect, useMemo, useRef, useState } from 'react';

import { StyledFieldset, StyledInput, StyledLabelPlaceholder, StyledInputWrapper, StyledWarning } from './input.styles';

import { InputProps } from './input.types';
import ErrorIcon from './error-icon';

const Input: FC<InputProps> = ({
	id,
	type,
	disabled,
	label,
	value,
	name,
	onChange,
	onBlur,
	title,
	validationMessage,
	extraSign,
	autoFillCb,
	autoFocus,
	placeholder,
	pattern,
	children,
	automationClass = '',
	forcePlaceholder = false,
	autoComplete = 'nope',
	hideDefaultValidationMessage,
	hideErrorIcon,
	onFocus,
	onChangeCallback,
	onFocusCallback,
	onBlurCallback,
	shake,
	extraSignClassName,
	noPadding = false,
	normalize,
	skipKeyboardEnterOnMobile = false,
	focusNext = false,
	...rest
}) => {
	const inputRef = useRef<HTMLInputElement>(null);
	const [focused, setFocused] = useState(false);
	const inputId = useMemo(() => id || `${Math.floor(Math.random() * 100000000000)}`, [id]);
	/* This is hacky way to detect when webkit browsers fex chrome, auto fill input fields
	We add animation on a webkit pseudo class that is added on auto fill.
	Read more on https://stackoverflow.com/a/41530164 */
	const handleAutoFill = (e: AnimationEvent<HTMLInputElement>) => {
		const autoFill = e.type === 'animationstart';
		if (autoFillCb) {
			autoFillCb(autoFill);
		}
	};

	const handleInputFocus = (e: FocusEvent<HTMLInputElement, Element>) => {
		setFocused(true);
		if (onFocus) {
			onFocus(e);
		}

		if (onFocusCallback) {
			onFocusCallback(e);
		}
	};

	const handleInputBlur = (e: FocusEvent<HTMLInputElement, Element>) => {
		setFocused(false);
		if (onBlur) {
			onBlur(e);
		}

		if (onBlurCallback) {
			onBlurCallback(e);
		}
	};

	const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
		if (onChange) {
			if (normalize && /^(NFC|NFD|NFKC|NFKD)$/.test(normalize)) {
				e.target.value = e.target.value.normalize(normalize);
			}
			onChange(e);
		}

		if (onChangeCallback) {
			onChangeCallback(e);
		}
	};

	useEffect(() => {
		if (autoFocus && inputRef) {
			inputRef.current?.focus();
			inputRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' });
		}
	}, [autoFocus]);

	// When user press Enter on mobile it should focus next input
	const goToNextInput = (event: KeyboardEvent<HTMLInputElement>) => {
		const isMobile = /iPhone|iPad|iPod|Android/i.test(navigator.userAgent) || focusNext;

		if (event.key === 'Enter' && isMobile) {
			event.preventDefault();
			const form = document.getElementById(id)?.closest('form');
			// Get all form inputs
			const formInputs = Array.from(form?.querySelectorAll('input, textarea, select') || []);

			const indexOfCurrentInput = formInputs.findIndex(input => input.id === id);

			if (skipKeyboardEnterOnMobile) {
				const currentInput = formInputs[indexOfCurrentInput] as HTMLInputElement;

				currentInput.blur();
			}
			// If this is last input in form submit it
			else if (formInputs.length && indexOfCurrentInput === formInputs.length - 1) {
				form?.requestSubmit();
			} else {
				const nextInput = formInputs[indexOfCurrentInput + 1] as HTMLInputElement;

				nextInput?.focus();
			}
		}
	};

	// handle when type is number and value is 0
	const hasValue = type === 'number' ? !!value?.toString() : !!value;

	let ph = label ? '' : placeholder;
	ph = forcePlaceholder && focused ? placeholder : ph;

	return (
		<StyledFieldset className={automationClass} data-at={`fieldset-${id}`}>
			<StyledInputWrapper extraSign={extraSign} visible={!!focused || hasValue} focused={focused}>
				<StyledInput
					id={inputId}
					type={type}
					data-at={`input-${id}`}
					onFocus={handleInputFocus}
					onBlur={handleInputBlur}
					value={value}
					onChange={handleChange}
					name={name || id}
					ref={inputRef}
					title={title || validationMessage || undefined}
					onAnimationStart={handleAutoFill}
					disabled={disabled}
					pattern={pattern}
					placeholder={ph}
					validationMessage={!!validationMessage}
					extraSign={extraSign}
					onKeyDown={goToNextInput}
					autoFocus={autoFocus}
					autoComplete={autoComplete}
					noPadding={noPadding}
					{...rest}
				/>
				<StyledLabelPlaceholder top={focused || hasValue} disabled={disabled} data-at={`label-${id}`}>
					{label || ''}
				</StyledLabelPlaceholder>
				{children}
				{validationMessage && !hideErrorIcon && <ErrorIcon shake={shake} />}
				{typeof extraSign !== 'string' && (
					<span className={`${extraSignClassName ?? ''} u-absolute`}>{extraSign}</span>
				)}
			</StyledInputWrapper>
			{validationMessage && !hideDefaultValidationMessage && (
				<StyledWarning className={`${automationClass}-validation-warning`} data-at={`validation-message-${id}`}>
					{validationMessage}
				</StyledWarning>
			)}
		</StyledFieldset>
	);
};

export default Input;
